/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   CollectionJobLogsPage.tsx
 *     Feature         :   143-collection-job-decomposition
 *     Description     :   Collection job execution log viewer — runs, steps, entries
 *     Constitution    :   v1.12.0 | Principle XXXVII (Code Headers)
 *     Author          :   Peter Dobler
 *     Created         :   2026-05-10
 *     Modified        :   2026-06-14 | Conformed to Spec 197 design blueprint
 * =========================================================================================
 */

import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronRight,
  ChevronDown,
  Clock,
  Activity,
} from 'lucide-react';
import { apiClient } from '../../services/apiClient';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Badge,
  EmptyState,
  ErrorState,
  LoadingState,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';
import { formatEastern } from '@/utils/dateFormatter';

// ============================================================================
// Types (mirrors FcjlogsRepository interfaces)
// ============================================================================

interface FcjlogsRunSummary {
  job_run_id: string;
  job_name: string;
  environment: string;
  started_at: string;
  completed_at: string | null;
  status: 'Running' | 'Completed' | 'Partial' | 'Failed';
  total_steps: number | null;
  completed_steps: number | null;
  failed_steps: number | null;
  entities_discovered: number | null;
  entities_updated: number | null;
  snapshots_created: number | null;
  errors_count: number | null;
  warnings_count: number | null;
}

interface FcjlogsStepSummary {
  step_name: string;
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
  total_entries: number;
  info_count: number;
  warning_count: number;
  error_count: number;
  critical_count: number;
  entities_processed: number;
  unique_entity_types: number;
}

interface FcjlogsActivityEntry {
  id: string;
  step_name: string;
  activity_type: string;
  severity: 'DEBUG' | 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
  message: string;
  entity_type: string | null;
  entity_id: string | null;
  duration_ms: number | null;
  http_status: number | null;
  record_count: number | null;
  error_detail: string | null;
  created_at: string;
}

interface Pagination {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

// ============================================================================
// Formatting helpers
// ============================================================================

function formatDurationMs(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}m ${s}s`;
}

function formatDurationBetween(a: string | null, b: string | null): string {
  if (!a || !b) return '—';
  return formatDurationMs(new Date(b).getTime() - new Date(a).getTime());
}

function formatTimestamp(ts: string | null): string {
  if (!ts) return '—';
  return formatEastern(ts);
}

function formatRelativeTime(ts: string | null): string {
  if (!ts) return '—';
  const sec = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

// ============================================================================
// Status / severity → Badge variant
// ============================================================================

type BadgeVariant = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  Running: 'info',
  Completed: 'success',
  Partial: 'warning',
  Failed: 'error',
};

const SEVERITY_VARIANT: Record<string, BadgeVariant> = {
  DEBUG: 'secondary',
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  CRITICAL: 'error',
};

// ============================================================================
// API query hooks
// ============================================================================

const PAGE_SIZE = 20;

function useRuns(offset: number) {
  return useQuery({
    queryKey: ['fcjlogs', 'runs', offset],
    queryFn: async () => {
      const res = await apiClient.get(
        `/api/operations/fcjlogs/runs?limit=${PAGE_SIZE}&offset=${offset}`
      );
      return res.data.data as { runs: FcjlogsRunSummary[]; pagination: Pagination };
    },
    refetchInterval: 30_000,
  });
}

function useSteps(runId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['fcjlogs', 'steps', runId],
    queryFn: async () => {
      const res = await apiClient.get(`/api/operations/fcjlogs/runs/${runId}/steps`);
      return res.data.data as { steps: FcjlogsStepSummary[] };
    },
    enabled,
  });
}

function useEntries(
  runId: string,
  stepName: string,
  severity: string | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: ['fcjlogs', 'entries', runId, stepName, severity ?? 'all'],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '100' });
      if (severity && severity !== 'all') params.set('severity', severity);
      const res = await apiClient.get(
        `/api/operations/fcjlogs/runs/${runId}/steps/${encodeURIComponent(stepName)}/entries?${params}`
      );
      return res.data.data as { entries: FcjlogsActivityEntry[]; pagination: Pagination };
    },
    enabled,
  });
}

// ============================================================================
// LogEntriesPanel — loaded per step expansion (literal log console)
// ============================================================================

const SEVERITY_OPTIONS = ['all', 'DEBUG', 'INFO', 'WARNING', 'ERROR', 'CRITICAL'];

const LogEntriesPanel: React.FC<{ runId: string; stepName: string }> = ({ runId, stepName }) => {
  const [severity, setSeverity] = useState('all');
  const { data, isLoading } = useEntries(
    runId,
    stepName,
    severity === 'all' ? undefined : severity,
    true
  );

  return (
    <div className="mx-4 mb-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-3">
      {/* Severity filter */}
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-xs text-[var(--color-text-secondary)]">Filter:</span>
        {SEVERITY_OPTIONS.map((s) => (
          <Button
            key={s}
            size="sm"
            variant={severity === s ? 'primary' : 'outline'}
            onClick={() => setSeverity(s)}
          >
            {s}
          </Button>
        ))}
        {data && (
          <span className="ml-auto text-xs text-[var(--color-text-secondary)]">
            {data.entries.length} / {data.pagination.total} entries
          </span>
        )}
      </div>

      {isLoading && <LoadingState rows={3} />}

      {data?.entries.length === 0 && !isLoading && (
        <p className="py-3 text-center text-sm text-[var(--color-text-secondary)]">
          No entries match this filter.
        </p>
      )}

      <div className="space-y-0.5">
        {data?.entries.map((entry) => (
          <div
            key={entry.id}
            className="flex items-start gap-2 border-b border-[var(--color-border)] py-1.5 font-mono text-xs last:border-0"
          >
            <Badge size="sm" variant={SEVERITY_VARIANT[entry.severity] ?? 'secondary'}>
              {entry.severity.slice(0, 4)}
            </Badge>
            <span className="hidden w-36 shrink-0 text-[var(--color-text-secondary)] sm:block">
              {formatTimestamp(entry.created_at)}
            </span>
            <span className="flex-1 leading-relaxed break-words text-[var(--color-text-primary)]">
              {entry.message}
            </span>
            {entry.duration_ms !== null && (
              <span className="shrink-0 text-[var(--color-text-secondary)]">
                {formatDurationMs(entry.duration_ms)}
              </span>
            )}
            {entry.record_count !== null && (
              <span className="shrink-0 text-[var(--color-text-secondary)]">
                {entry.record_count} rows
              </span>
            )}
            {entry.error_detail && (
              <span
                className="max-w-xs shrink-0 truncate text-[var(--color-status-danger)]"
                title={entry.error_detail}
              >
                {entry.error_detail}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// StepsPanel — loaded per run expansion
// ============================================================================

const StepsPanel: React.FC<{ runId: string }> = ({ runId }) => {
  const { data, isLoading } = useSteps(runId, true);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());

  const toggleStep = (name: string) =>
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });

  if (isLoading) {
    return (
      <div className="ml-8 py-3">
        <LoadingState rows={2} />
      </div>
    );
  }

  if (!data?.steps?.length) {
    return (
      <p className="ml-8 py-3 text-sm text-[var(--color-text-secondary)]">
        No steps recorded for this run.
      </p>
    );
  }

  return (
    <div className="mb-2 ml-8 space-y-0.5 border-l-2 border-[var(--color-border)] pl-2">
      {data.steps.map((step) => {
        const isExpanded = expandedSteps.has(step.step_name);
        const errorTotal = step.error_count + step.critical_count;

        return (
          <div key={step.step_name}>
            <button
              className="flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm hover:bg-[var(--color-bg-subtle)]"
              onClick={() => toggleStep(step.step_name)}
            >
              {isExpanded ? (
                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-secondary)]" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-secondary)]" />
              )}
              <span
                className="w-52 truncate font-medium text-[var(--color-text-primary)]"
                title={step.step_name}
              >
                {step.step_name}
              </span>
              <span className="w-16 shrink-0 text-right text-xs text-[var(--color-text-secondary)]">
                {formatDurationMs(step.duration_ms)}
              </span>
              <span className="w-20 shrink-0 text-right text-xs text-[var(--color-text-secondary)]">
                {step.total_entries} entries
              </span>
              <div className="ml-2 flex items-center gap-1">
                {step.warning_count > 0 && (
                  <Badge size="sm" variant="warning">
                    {step.warning_count}W
                  </Badge>
                )}
                {errorTotal > 0 && (
                  <Badge size="sm" variant="error">
                    {errorTotal}E
                  </Badge>
                )}
              </div>
              <span className="ml-auto text-xs text-[var(--color-text-secondary)]">
                {step.entities_processed > 0 ? `${step.entities_processed} entities` : ''}
              </span>
            </button>
            {isExpanded && <LogEntriesPanel runId={runId} stepName={step.step_name} />}
          </div>
        );
      })}
    </div>
  );
};

// ============================================================================
// CollectionJobLogsPage
// ============================================================================

const CollectionJobLogsPage: React.FC = () => {
  const [offset, setOffset] = useState(0);
  const [expandedRuns, setExpandedRuns] = useState<Set<string>>(new Set());

  const { data, isLoading, isFetching, error, refetch } = useRuns(offset);
  const queryClient = useQueryClient();

  const toggleRun = (runId: string) =>
    setExpandedRuns((prev) => {
      const next = new Set(prev);
      next.has(runId) ? next.delete(runId) : next.add(runId);
      return next;
    });

  const runs = data?.runs ?? [];
  const pagination = data?.pagination;

  const columns: DataTableColumn<FcjlogsRunSummary>[] = [
    {
      key: 'run',
      header: 'Run / Job',
      width: '34%',
      render: (run) => {
        const isExpanded = expandedRuns.has(run.job_run_id);
        return (
          <div className="flex min-w-0 items-center gap-2">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 shrink-0 text-[var(--color-text-secondary)]" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0 text-[var(--color-text-secondary)]" />
            )}
            <div className="min-w-0">
              <div className="truncate font-medium text-[var(--color-text-primary)]">
                {run.job_name || 'collection-job'}
              </div>
              <div className="truncate font-mono text-xs text-[var(--color-text-secondary)]">
                {run.job_run_id.slice(0, 18)}… · {run.environment}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'started',
      header: 'Started',
      width: '16%',
      render: (run) => (
        <div>
          <div className="text-[var(--color-text-primary)]">{formatRelativeTime(run.started_at)}</div>
          <div className="text-xs text-[var(--color-text-secondary)]">
            {formatTimestamp(run.started_at)}
          </div>
        </div>
      ),
    },
    {
      key: 'duration',
      header: 'Duration',
      width: '12%',
      render: (run) => (
        <div className="flex items-center gap-1 text-[var(--color-text-secondary)]">
          <Clock className="h-3 w-3" />
          <span>{formatDurationBetween(run.started_at, run.completed_at)}</span>
        </div>
      ),
    },
    {
      key: 'steps',
      header: 'Steps',
      width: '12%',
      align: 'center',
      render: (run) =>
        run.total_steps != null ? (
          <span
            className={
              (run.failed_steps ?? 0) > 0
                ? 'font-medium text-[var(--color-status-danger)]'
                : 'text-[var(--color-text-secondary)]'
            }
          >
            {/* Spec 298 FR-006 (row 67): completed_steps is a NULL INT the run may not have recorded. */}
            {run.completed_steps != null ? (
              run.completed_steps
            ) : (
              <span title="Steps completed not recorded">—</span>
            )}
            /{run.total_steps}
            {(run.failed_steps ?? 0) > 0 && (
              <span className="ml-1 text-[var(--color-status-danger)]">({run.failed_steps}✗)</span>
            )}
          </span>
        ) : (
          <span className="text-[var(--color-text-secondary)]">—</span>
        ),
    },
    {
      key: 'entities',
      header: 'Entities',
      width: '14%',
      align: 'center',
      render: (run) =>
        run.entities_discovered != null ? (
          <span
            // Spec 298 FR-006 (row 68): both are NULL INT columns the run may not have recorded.
            title={`${run.entities_updated != null ? `${run.entities_updated} updated` : 'updated count not recorded'} · ${
              run.snapshots_created != null ? `${run.snapshots_created} snapshots` : 'snapshot count not recorded'
            }`}
          >
            <span className="text-[var(--color-text-primary)]">
              {run.entities_discovered.toLocaleString()}
            </span>
            {(run.errors_count ?? 0) > 0 && (
              <span className="ml-1 text-[var(--color-status-danger)]">{run.errors_count}E</span>
            )}
            {(run.warnings_count ?? 0) > 0 && (
              <span className="ml-1 text-[var(--color-status-warning)]">{run.warnings_count}W</span>
            )}
          </span>
        ) : (
          <span className="text-[var(--color-text-secondary)]">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '12%',
      align: 'center',
      render: (run) => (
        <Badge variant={STATUS_VARIANT[run.status] ?? 'secondary'}>{run.status}</Badge>
      ),
    },
  ];

  return (
    <PageLayout
      title="Collection Job Logs"
      description="Execution history, step details, and structured log entries for all collection job runs"
      icon={<Activity className="h-6 w-6" />}
      helpId="internal.collection-job-logs"
      // Spec 285 / 226: the standard Refresh replaces the runs-only button — it reloads the runs,
      // and the steps and log entries of any expanded run (all share the 'fcjlogs' key prefix).
      onRefresh={() => queryClient.invalidateQueries({ queryKey: ['fcjlogs'] })}
      isRefreshing={isFetching}
    >
      {error && !isLoading ? (
        <ErrorState
          title="Failed to load collection job runs"
          message="Verify connection and Operator permissions."
          onRetry={() => void refetch()}
        />
      ) : isLoading ? (
        <Card>
          <LoadingState rows={6} />
        </Card>
      ) : runs.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Activity className="h-10 w-10" />}
            title="No collection job runs found"
            message="Runs appear here after a collection tier executes."
          />
        </Card>
      ) : (
        <Card padding="none">
          {/* Runs are an expandable master/detail accordion: the DataTable lists the
              runs; clicking a row toggles its nested steps → log-entry console. */}
          <DataTable
            columns={columns}
            rows={runs}
            rowKey={(run) => run.job_run_id}
            minWidth="52rem"
            /* This page owns pagination (offset/limit against the server, Previous/Next
               below), so the table must not render a second, inert one — see the
               blueprint's DataTable rules. Same defect as the Collection Jobs and
               Infrastructure Sync run histories. */
            showPagination={false}
            onRowClick={(run) => toggleRun(run.job_run_id)}
            emptyMessage="No collection job runs found"
          />

          {/* Expanded run detail panels */}
          {runs
            .filter((run) => expandedRuns.has(run.job_run_id))
            .map((run) => (
              <div key={run.job_run_id} className="border-t border-[var(--color-border)] py-2">
                <div className="px-4 py-1 text-xs font-semibold text-[var(--color-text-secondary)]">
                  {run.job_name || 'collection-job'} · {run.job_run_id.slice(0, 18)}…
                </div>
                <StepsPanel runId={run.job_run_id} />
              </div>
            ))}

          {/* Server pagination */}
          {pagination && (pagination.hasMore || offset > 0) && (
            <div className="flex items-center justify-between border-t border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-4 py-3">
              <span className="text-xs text-[var(--color-text-secondary)]">
                {offset + 1}–{Math.min(offset + PAGE_SIZE, pagination.total)} of {pagination.total}{' '}
                runs
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  disabled={offset === 0}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  disabled={!pagination.hasMore}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </PageLayout>
  );
};

export default CollectionJobLogsPage;
